import { Component } from '@angular/core';
import { EventEmitter, Output  } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { alertService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { loginService } from '@unpispas/upp-data';

/**
 * Password-recovery screen shown from {@link UppLoginComponent} (`Page === 'RECOVERY'`).
 *
 * Collects the account email, calls `login/recover` via {@link loginService.DoRecover}, and
 * does not require a terminal id (`device` is omitted from the request). On success, shows a
 * confirmation alert and returns to the login view through {@link Cancel}.
 *
 * @see `upp-login.html` — `(Cancel)` and `(Accept)` on the parent both navigate back to login.
 */
@Component({
    selector: 'upp-recovery-page',
    templateUrl: './upp-recovery.html',
    styleUrls: [
      './upp-recovery.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UppRecoveryPageComponent extends languageSupport {
    /** Emitted when the user dismisses the success alert (same navigation as {@link Cancel}). */
    @Output() Accept = new EventEmitter<void>();

    /** Emitted when the user closes the screen (toolbar/back) or after a successful recovery flow. */
    @Output() Cancel = new EventEmitter<void>();

    /**
     * @param lang - Translation service (via {@link languageSupport}).
     * @param change - Change detector for OnPush updates.
     * @param alertCtrl - Ionic alerts for the post-recovery confirmation.
     * @param login - Backend `login/recover` bridge.
     * @param view - Shared view/kiosk flags (template bindings).
     */
    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        private alertCtrl: alertService,
        private login: loginService,
        public view: viewService
    ){
        super(lang, change);
    }

    private _recoveryForm: FormGroup | null = null;

    /** Lazy-built reactive form with a single required email field (`usermail`). */
    get RecoveryForm() {
        if (this._recoveryForm == null){
            this._recoveryForm = new FormGroup({
                usermail: new FormControl(null, [
                    Validators.required,
                    Validators.email            
                ])
            });
        }

        return this._recoveryForm;
    }  

    /**
     * Resets the form and notifies the parent to show the login screen.
     */
    CloseView(): void {
        this.RecoveryForm.reset();
        this.Cancel.next();
    }

    /**
     * Submits the recovery request for the email entered in {@link RecoveryForm}.
     *
     * On API success, opens {@link EmailSentAlert}; errors are surfaced by {@link loginService}
     * (toast). Does nothing when the email control is empty.
     */
    DoRecover(): void {
        const _username = this.RecoveryForm.get('usermail')?.value;
        if (_username){
            this.login.DoRecover(_username).then(
            (success) => {
                if (success){
                    this.EmailSentAlert(_username);
                }
            });
        }
    }

    /**
     * Shows a localized alert that the recovery email was sent, then calls {@link CloseView}.
     *
     * @param username - Email address shown in the message (same value sent to the API).
     */
    private async EmailSentAlert(username: string): Promise <void> {
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@recover_email_sent'),
            message: this.lang.tr('@recover_email_check') + ": <strong>" + username + "</strong>",
            buttons: [{
                text: this.lang.tr('@ok'),
                handler: () => {
                    alert.dismiss(true);
                }
            }]
        });
    
        alert.onDidDismiss().then(
        () => {
            setTimeout(() => {
                this.CloseView();
            }, 100);
        });    

        await alert.present();
    }
}
